import React, { useCallback, useState, useEffect } from 'react';
import * as ReactDOMClient from 'react-dom/client';
import { WidgetsProvider, WidgetDataType, usePreviewSearch, widget } from '@sitecore-search/react';
import { PreviewSearch } from '@sitecore-search/ui';
import PropTypes from 'prop-types';

import GoogleIcon from '../google-icon';
import SuggestionBlock from '../sitecore-search-components/c-search-suggestion-block-sitecore';
import SearchFormSitecore from '../sitecore-search-components/c-search-form-sitecore';

function SearchBarSitecore({ defaultItemsPerPage = 6, searchUrl = '/search-results', dataSourceId }) {
    /*
    Due to the current limitations of the website,
    it is best to simply observe the parent search bar
    component for open / closed updates
    */

    const [searchQuery, setSearchQuery] = useState('');
    const [isSearchBarOpen, setIsSearchBarOpen] = useState(false);

    useEffect(() => {
        // Get the parent container
        const parentContainer = document.querySelector('[data-sitecore-search-bar]');
        if (!parentContainer) return;
        // Create observer to watch for class changes
        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    setIsSearchBarOpen(parentContainer.classList.contains('open'));
                }
            });
        });

        // Start observing
        observer.observe(parentContainer, {
            attributes: true,
            attributeFilter: ['class']
        });

        // Cleanup
        /* eslint-disable consistent-return */
        return () => {
            observer.disconnect();
        };
        /* eslint-disable consistent-return */
    }, []);

    const {
        actions: { onKeyphraseChange },
        queryResult: {
            isFetching,
            isLoading,
            data: {
                suggestion: {
                    title_context_aware: articleSuggestions = [],
                    user_search_history: userSearchHistory = [],
                    popular_search_page: popularSearches = [],
                    did_you_mean_title: autoCorrectSuggestions = []
                } = {}
            } = {}
        }
    } = usePreviewSearch({
        query: query => {
            query.getRequest().setSources([dataSourceId]);
        },
        state: {
            suggestionsList: [
                { suggestion: 'title_context_aware', max: defaultItemsPerPage },
                { suggestion: 'user_search_history', max: defaultItemsPerPage },
                { suggestion: 'popular_search_page', max: defaultItemsPerPage },
                { suggestion: 'did_you_mean_title', max: 1 }
            ],
            itemsPerPage: defaultItemsPerPage
        }
    });

    const loading = isLoading || isFetching;
    const keyphraseHandler = useCallback(
        event => {
            const { target } = event;
            setSearchQuery(target.value);
            onKeyphraseChange({ keyphrase: target.value });
        },
        [onKeyphraseChange]
    );

    const handleSubmit = () => {
        const targetReplace = `${searchUrl}?q=${searchQuery}`;
        window.location.href = targetReplace;
    };

    const onClickClose = () => {
        document.querySelector('[data-sitecore-search-bar]').classList.remove('open');
    };

    return (
        <div className="search-bar-sitecore-parent">
            <div className="search-bar-sitecore-content">
                <div className="search-bar-container">
                    <div className="search-bar-nav">
                        <button className="search-bar-nav-button search-bar-close" type="button" onClick={() => onClickClose()}>
                            <GoogleIcon icon="close" />
                            Close
                        </button>
                    </div>
                    <PreviewSearch.Root>
                        <SearchFormSitecore
                            keyphraseHandler={keyphraseHandler}
                            searchQuery={searchQuery}
                            handleSubmit={handleSubmit}
                            onClickClear={() => {
                                setSearchQuery('');
                            }}
                        />
                        {isSearchBarOpen && (
                            <div className="search-bar-suggestions-container">
                                {searchQuery !== '' ? (
                                    <PreviewSearch.Suggestions className="search-bar-sitecore-suggestions search-bar-suggestion">
                                        <SuggestionBlock blockId="search-bar-sitecore-desktop-suggestions" className="search-suggestions" items={articleSuggestions} title="Suggested Searches" loading={loading} redirectUrl={searchUrl} />
                                        <PreviewSearch.SuggestionsGroup className="search-bar-sitecore-search-text-trigger">
                                            <PreviewSearch.SuggestionTrigger>
                                                {autoCorrectSuggestions.length > 0 ? (
                                                    <a className="search-bar-sitecore-search-query" href={`${searchUrl}?q=${autoCorrectSuggestions[0].text}`}>
                                                        Did you mean
                                                        {' '}
                                                        &quot;{autoCorrectSuggestions[0].text}&quot; ?
                                                        <GoogleIcon icon="arrow_forward" />
                                                    </a>
                                                ) : (
                                                    <a className="search-bar-sitecore-search-query" href={`${searchUrl}?q=${searchQuery}`}>
                                                        Search for
                                                        {' '}
                                                        &quot;{searchQuery}&quot;
                                                        <GoogleIcon icon="arrow_forward" />
                                                    </a>
                                                )}
                                            </PreviewSearch.SuggestionTrigger>
                                        </PreviewSearch.SuggestionsGroup>
                                    </PreviewSearch.Suggestions>
                                )
                                    : (
                                        <>
                                            {
                                                popularSearches.length > 0 && (
                                                    <PreviewSearch.Suggestions className="search-bar-sitecore-suggestions search-bar-popular">
                                                        <SuggestionBlock blockId="search-bar-sitecore-desktop-popular" className="search-suggestions" items={popularSearches} title="Popular Searches" loading={loading} redirectUrl={searchUrl} />
                                                    </PreviewSearch.Suggestions>
                                                )
                                            }
                                            {
                                                userSearchHistory.length > 0 && (
                                                    <PreviewSearch.Suggestions className="search-bar-sitecore-suggestions search-bar-history">
                                                        <SuggestionBlock prefixIcon="history" blockId="search-bar-sitecore-desktop-history" className="search-suggestions" items={userSearchHistory} title="Search History" loading={loading} redirectUrl={searchUrl} />
                                                    </PreviewSearch.Suggestions>
                                                )
                                            }
                                        </>
                                    )}
                            </div>
                        )}
                    </PreviewSearch.Root>
                </div>
            </div>
            <div className={`search-overlay search-overlay-sitecore ${isSearchBarOpen ? 'is-visible' : ''}`} onClick={() => onClickClose()} aria-hidden="true" />
        </div>

    );
}

SearchBarSitecore.propTypes = {
    defaultItemsPerPage: PropTypes.number.isRequired,
    searchUrl: PropTypes.string.isRequired,
    dataSourceId: PropTypes.string.isRequired
};

const PreviewSearchWidget = widget(SearchBarSitecore, WidgetDataType.PREVIEW_SEARCH, 'content');

const SearchBarSitecoreInit = ({ el, props }) => {
    const {
        env = 'apse2',
        customerKey = '186718190-16785702162',
        apiKey = '01-3dab73b8-a842cb674e5998facb9344effba40e34541a4e04',
        dataSourceId = '1190926',
        searchUrl = '/search-results',
        previewSearchWidgetId = 'rfkid_7'
    } = props;

    const root = ReactDOMClient.createRoot(el);
    root.render(
        <WidgetsProvider
            env={env}
            customerKey={customerKey}
            apiKey={apiKey}
            publicSuffix={true}
        >
            <PreviewSearchWidget defaultItemsPerPage={6} rfkId={previewSearchWidgetId} searchUrl={searchUrl} dataSourceId={dataSourceId}/>
        </WidgetsProvider>
    );
};

export default SearchBarSitecoreInit;
